/**
 * Standards Selector Component
 *
 * Allows users to browse and select standards from .equilateral-standards/
 * as templates for document creation.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Label, Badge, Spinner, Alert, TextInput } from 'flowbite-react';
import {
  HiSearch,
  HiFolder,
  HiDocument,
  HiChevronRight,
  HiChevronLeft,
  HiCheckCircle
} from 'react-icons/hi';
import {
  listStandardCategories,
  listStandardsInCategory,
  getStandardContent,
  type StandardCategory,
  type StandardFile,
  type StandardContent
} from '../services/api/standards';

// Types are imported from the API service

interface StandardsSelectorProps {
  onSelect: (standard: StandardContent) => void;
  onCancel: () => void;
  className?: string;
}

// Standard categories configuration
const STANDARD_CATEGORIES: Record<string, { displayName: string; description: string }> = {
  'serverless-saas-aws': {
    displayName: 'Serverless SaaS AWS',
    description: 'AWS Lambda, API Gateway, SAM patterns and standards'
  },
  'frontend-development': {
    displayName: 'Frontend Development',
    description: 'React, TypeScript, UI/UX patterns and components'
  },
  'multi-agent-orchestration': {
    displayName: 'Multi-Agent Orchestration',
    description: 'Agent coordination, workflows, and communication protocols'
  },
  'compliance-security': {
    displayName: 'Compliance & Security',
    description: 'Security, privacy, compliance and audit standards'
  },
  'cost-optimization': {
    displayName: 'Cost Optimization',
    description: 'Cost management and resource optimization patterns'
  },
  'database': {
    displayName: 'Database Patterns',
    description: 'PostgreSQL, data modeling and query optimization'
  },
  'backend': {
    displayName: 'Backend Development',
    description: 'Backend architecture and development patterns'
  },
  'testing': {
    displayName: 'Testing Standards',
    description: 'Testing strategies, patterns and best practices'
  },
  'deployment': {
    displayName: 'Deployment',
    description: 'Deployment strategies and infrastructure patterns'
  },
  'real-time-systems': {
    displayName: 'Real-Time Systems',
    description: 'WebSocket, real-time communication patterns'
  }
};

export const StandardsSelector: React.FC<StandardsSelectorProps> = ({
  onSelect,
  onCancel,
  className = ''
}) => {
  const [view, setView] = useState<'categories' | 'files' | 'preview'>('categories');
  const [categories, setCategories] = useState<StandardCategory[]>([]);
  const [files, setFiles] = useState<StandardFile[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<StandardFile | null>(null);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format category name for display
  const formatCategoryName = (category: string): string => {
    return STANDARD_CATEGORIES[category]?.displayName ||
           category.split('-').map(word =>
             word.charAt(0).toUpperCase() + word.slice(1)
           ).join(' ');
  };

  // Format filename for display
  const formatFilename = (filename: string): string => {
    return filename
      .replace('.md', '')
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Load available categories
  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await listStandardCategories();

        if (response.success && response.data) {
          setCategories(response.data);
        } else {
          // Fail loud - no fallback data
          throw new Error(response.message || 'Failed to load standards categories');
        }
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError(err instanceof Error ? err.message : 'Failed to load standards categories');
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Load files for selected category
  const loadCategoryFiles = useCallback(async (category: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await listStandardsInCategory(category);

      if (response.success && response.data) {
        setFiles(response.data);
      } else {
        setError(response.message || 'Failed to load standards files');
        setFiles([]);
      }

      setSelectedCategory(category);
      setView('files');
    } catch (err) {
      console.error('Failed to load files:', err);
      setError('Failed to load standards files');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load file content for preview
  const loadFileContent = useCallback(async (file: StandardFile) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getStandardContent(file.path);

      if (response.success && response.data) {
        setSelectedFile(file);
        setPreviewContent(response.data.content);
        setView('preview');
      } else {
        setError(response.message || 'Failed to load standard content');
      }
    } catch (err) {
      console.error('Failed to load file content:', err);
      setError('Failed to load standard content');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle standard selection
  const handleSelect = useCallback(() => {
    if (!selectedFile || !previewContent) return;

    const standardContent: StandardContent = {
      filename: selectedFile.filename,
      content: previewContent,
      category: selectedFile.category,
      path: selectedFile.path
    };

    onSelect(standardContent);
  }, [selectedFile, previewContent, onSelect]);

  // Go back to previous view
  const handleBack = useCallback(() => {
    if (view === 'preview') {
      setView('files');
      setSelectedFile(null);
      setPreviewContent(null);
    } else if (view === 'files') {
      setView('categories');
      setSelectedCategory(null);
      setFiles([]);
    }
  }, [view]);

  // Filter categories based on search
  const filteredCategories = categories.filter(cat =>
    cat.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter files based on search
  const filteredFiles = files.filter(file =>
    file.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    file.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && categories.length === 0) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
          <span className="ml-3">Loading standards...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`p-6 ${className}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {view === 'categories' && 'Browse Standards'}
              {view === 'files' && formatCategoryName(selectedCategory || '')}
              {view === 'preview' && 'Standard Preview'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {view === 'categories' && 'Select a category to browse available standards'}
              {view === 'files' && 'Select a standard to preview'}
              {view === 'preview' && 'Review standard content before creating document'}
            </p>
          </div>
          {view !== 'categories' && (
            <Button size="sm" color="light" onClick={handleBack}>
              <HiChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert color="failure" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search */}
        <div>
          <TextInput
            icon={HiSearch}
            placeholder={`Search ${view === 'categories' ? 'categories' : 'standards'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Categories View */}
        {view === 'categories' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
            {filteredCategories.map((category) => (
              <button
                key={category.name}
                onClick={() => loadCategoryFiles(category.name)}
                className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <HiFolder className="w-5 h-5 text-blue-500 mt-1" />
                    <div>
                      <h3 className="font-medium text-gray-900 group-hover:text-blue-600">
                        {category.displayName}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {category.description}
                      </p>
                    </div>
                  </div>
                  <HiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Files View */}
        {view === 'files' && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredFiles.length === 0 ? (
              <div className="text-center py-8">
                <HiDocument className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">
                  {searchQuery ? 'No standards match your search' : 'No standards available in this category'}
                </p>
              </div>
            ) : (
              filteredFiles.map((file) => (
                <button
                  key={file.path}
                  onClick={() => loadFileContent(file)}
                  className="w-full p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <HiDocument className="w-5 h-5 text-gray-400 group-hover:text-blue-500" />
                    <div>
                      <h4 className="font-medium text-gray-900 group-hover:text-blue-600">
                        {file.displayName}
                      </h4>
                      <p className="text-xs text-gray-500">{file.filename}</p>
                    </div>
                  </div>
                  <HiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500" />
                </button>
              ))
            )}
          </div>
        )}

        {/* Preview View */}
        {view === 'preview' && (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">
                    {selectedFile?.displayName}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedFile?.path}
                  </p>
                </div>
                <Badge color="blue">{formatCategoryName(selectedFile?.category || '')}</Badge>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-80 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                {previewContent}
              </pre>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This standard will be used as the initial content for your document.
                You can edit it after creation.
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button color="gray" onClick={onCancel}>
            Cancel
          </Button>
          {view === 'preview' && (
            <Button color="blue" onClick={handleSelect}>
              <HiCheckCircle className="w-4 h-4 mr-2" />
              Use This Standard
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default StandardsSelector;
